import { OptionsApi } from '@core/swagger-client';
import { Chapter, WebinarStatus } from '@core/types';
import { CompositePageHeader, ContainerNoTopSpace, ContainerWebinarInner, ContentHeader, LoaderPage, PageHeader, PageHeaderContent } from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AddChapterButton,
    ChapterIndex,
    ChapterSlideNumberInput,
    ChapterTitleHeader,
    ChapterTitleInput,
    DeleteChapterButton,
    SlideTitleHeader
} from '../../atoms';
import { FormBody } from '../../molecules';

export const ChapterManagement = () => {
    const { webinarId } = useParams({ strict: false });
    const { t } = useTranslation();
    const [chapters, setChapters] = useState<Chapter[]>();
    const [saveState, setSaveState] = useState<'saved' | 'saving' | 'error' | 'none'>('saved');
    const [readOnly, setReadOnly] = useState<boolean>(false);

    const { data: webinar } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));
    const { data: webinarStatus } = useQuery(OptionsApi.webinar.getStatus(webinarId!));

    const { mutate: saveChapters } = useMutation(OptionsApi.chapter.update());

    useEffect(() => {
        setReadOnly(webinarStatus !== WebinarStatus.Planned && webinarStatus !== WebinarStatus.Started);
    }, [webinarStatus]);

    const { data: chaptersData } = useQuery(OptionsApi.chapter.get(webinarId!, { enabled: !chapters }));

    useEffect(() => {
        if (chaptersData) {
            setChapters(chaptersData);
        }
    }, [chaptersData]);

    useEffect(() => {
        if (readOnly) {
            setSaveState('none');
            return;
        }

        if (!chapters) {
            return;
        }

        setSaveState('saving');
        const handle = setTimeout(() => {
            if (readOnly) {
                return;
            }

            saveChapters({ webinarId: webinarId!, data: chapters }, { onSuccess: () => setSaveState('saved'), onError: () => setSaveState('error') });
        }, 1000);
        return () => clearTimeout(handle);
    }, [chapters, readOnly]);

    if (!webinar || !chapters) {
        return <LoaderPage />;
    }

    const addChapter = () => {
        let slideNumber = 1;
        if (chapters.length > 0) {
            const lastChapter = chapters[chapters.length - 1];
            slideNumber = lastChapter.slideToStartWith + 1;
        }
        const newChapter: Chapter = {
            chapterId: crypto.randomUUID(),
            chapterTitle: '',
            slideToStartWith: slideNumber
        };
        setChapters([...chapters, newChapter]);
    };

    const deleteChapter = (chapter: Chapter) => {
        const newChapters = chapters.filter((c) => c.chapterId !== chapter.chapterId);
        setChapters(newChapters);
    };

    const updateTitle = (chapter: Chapter, title: string) => {
        const newChapters = chapters.map((c) => {
            if (c.chapterId === chapter.chapterId) {
                return { ...c, chapterTitle: title };
            }
            return c;
        });
        setChapters(newChapters);
    };

    const updateSlide = (chapter: Chapter, slide: string) => {
        const newChapters = chapters.map((c) => {
            if (c.chapterId === chapter.chapterId) {
                return { ...c, slideToStartWith: parseInt(slide) };
            }
            return c;
        });
        setChapters(newChapters);
    };

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>{webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}</PageHeaderContent>
            </PageHeader>
            <ContainerWebinarInner>
                <BackToOriginLink fallback="content" />
                <div>
                    {saveState === 'saved' && <p className="text-success mb-4">{t('EditSurvey.Save.Saved')}</p>}
                    {saveState === 'saving' && <p className="text-orange-500 mb-4">{t('EditSurvey.Save.Saving')}</p>}
                    {saveState === 'error' && <p className="text-warning mb-4">{t('EditSurvey.Save.Error')}</p>}
                </div>
                <ContentHeader testId="chaptersList" contentHeader={t('Content.Chapters.PageHeading')} />
                <div className="flex-auto mt-5">
                    <FormBody>
                        <table className="w-full">
                            <thead>
                                <tr className="align-bottom">
                                    <th className="w-8" />
                                    <ChapterTitleHeader chapterHeader={t('Content.Chapters.ChapterTitleHeader')} />
                                    <SlideTitleHeader slideHeader={t('Content.Chapters.SlideNumberHeader')} />
                                    <th className="w-10" />
                                </tr>
                            </thead>

                            <tbody>
                                {chapters.map((chapter, index) => (
                                    <tr className="align-top">
                                        <ChapterIndex index={index + 1} key={`chapter-${chapter.chapterId}-index`} />
                                        <ChapterTitleInput
                                            key={`chapter-${chapter.chapterId}-title`}
                                            value={chapter.chapterTitle}
                                            onChange={(e) => updateTitle(chapter, e.target.value)}
                                            readOnly={readOnly}
                                        />
                                        <ChapterSlideNumberInput
                                            key={`chapter-${chapter.chapterId}-slide`}
                                            value={chapter.slideToStartWith}
                                            onChange={(e) => updateSlide(chapter, e.target.value)}
                                            readOnly={readOnly}
                                        />
                                        {!readOnly && (
                                            <DeleteChapterButton
                                                chapter={chapter}
                                                onDeleteChapterClick={(chapter) => deleteChapter(chapter)}
                                                key={`chapter-${chapter.chapterId}-delete`}
                                            />
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!readOnly && <AddChapterButton onClick={() => addChapter()} />}
                        {readOnly && <span>{t('Content.Chapters.ReadOnly')}</span>}
                    </FormBody>
                </div>
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
